/**
 * Payer Connections — staff roster helpers (AC-14, AC-15, AC-16, AC-17).
 *
 * Pure functions building on `foldPayers`. `asOf` is always an explicit
 * parameter — never `Date.now()` — so these are deterministic and testable.
 */

import { foldPayers, sortPayers } from './fold';
import {
  type BlockedParty,
  type FoldedPayer,
  type OrgEnrollmentInput,
  PARTY_ORDER,
  type PayerState,
  type RosterRow,
} from './types';

const PARTY_BY_STATE: Record<Exclude<PayerState, 'live'>, BlockedParty> = {
  needs_us: 'JOTPSYCH',
  needs_customer: 'CUSTOMER',
  waiting: 'PAYER',
  ready: 'NONE',
  canceled: 'NONE',
};

/**
 * AC-14: the blocked-on party is the party of the org's most severe payer.
 * An org with no non-live payers (nothing enrolling, or everything live) is NONE.
 */
export function blockedOnParty(payers: FoldedPayer[]): BlockedParty {
  if (payers.length === 0) return 'NONE';
  // payers is expected sorted by severity (foldPayers already sorts this way);
  // sort defensively so callers can pass an arbitrary order.
  const mostSevere = payers.slice().sort(sortPayers)[0];
  return PARTY_BY_STATE[mostSevere.state as Exclude<PayerState, 'live'>] ?? 'NONE';
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole UTC-date days between two dates, floored, never negative. */
function wholeDaysBetween(asOf: Date, anchor: Date): number {
  const asOfUTC = Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate());
  const anchorUTC = Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate());
  return Math.max(Math.floor((asOfUTC - anchorUTC) / 86_400_000), 0);
}

/**
 * AC-15: whole days since the oldest of Stedi's submitted_at or created_at,
 * across the most severe payer's rails. NEVER status_updated_at (every
 * status change rewrites it). Null when no such timestamp exists on any rail.
 *
 * When multiple payers tie at the org's most severe state, the anchor is
 * computed from the payer AC-8 sorting picks first (severity, then name) —
 * the same payer the roster chip strip would lead with.
 */
export function daysBlocked(payers: FoldedPayer[], asOf: Date): number | null {
  if (payers.length === 0) return null;
  const mostSevere = payers.slice().sort(sortPayers)[0];
  const rails = Object.values(mostSevere.rails).filter((r): r is NonNullable<typeof r> => !!r);

  let oldest: Date | null = null;
  for (const rail of rails) {
    for (const candidate of [parseDate(rail.submittedAt), parseDate(rail.createdAt)]) {
      if (candidate && (!oldest || candidate.getTime() < oldest.getTime())) {
        oldest = candidate;
      }
    }
  }

  if (!oldest) return null;
  return wholeDaysBetween(asOf, oldest);
}

/** Build one roster row for an organization (AC-14, AC-15, AC-16). */
export function buildRosterRow(input: OrgEnrollmentInput, asOf: Date): RosterRow {
  const { payers, liveCount, unknownCount } = foldPayers(input.rows);
  return {
    organizationId: input.organizationId,
    organizationName: input.organizationName,
    blockedParty: blockedOnParty(payers),
    daysBlocked: daysBlocked(payers, asOf),
    payers,
    liveCount,
    unknownCount,
  };
}

/** AC-17: sort by party rank (JOTPSYCH, CUSTOMER, PAYER, NONE), then days desc (null last), then org name. */
export function sortRoster(rows: RosterRow[]): RosterRow[] {
  return rows.slice().sort((a, b) => {
    const partyDiff = PARTY_ORDER.indexOf(a.blockedParty) - PARTY_ORDER.indexOf(b.blockedParty);
    if (partyDiff !== 0) return partyDiff;

    if (a.daysBlocked !== b.daysBlocked) {
      if (a.daysBlocked == null) return 1;
      if (b.daysBlocked == null) return -1;
      return b.daysBlocked - a.daysBlocked;
    }

    return a.organizationName.localeCompare(b.organizationName);
  });
}

/** One chip-strip entry: a payer's first two words plus its state, for the roster's compact chip row (AC-16). */
export interface RosterChip {
  payerId: string;
  label: string;
  state: PayerState;
}

const MAX_VISIBLE_CHIPS = 4;

/** AC-16: label = first two words of the payer name. */
export function firstTwoWords(payerName: string): string {
  return payerName.trim().split(/\s+/).slice(0, 2).join(' ');
}

/**
 * AC-16: chip strip for a roster row — up to 4 chips, then an overflow count.
 * Returns the visible chips and the overflow count (0 when everything fits).
 */
export function rosterChipStrip(payers: FoldedPayer[]): {
  chips: RosterChip[];
  overflowCount: number;
} {
  const visible = payers.slice(0, MAX_VISIBLE_CHIPS).map(p => ({
    payerId: p.payerId,
    label: firstTwoWords(p.payerName),
    state: p.state,
  }));
  const overflowCount = Math.max(payers.length - MAX_VISIBLE_CHIPS, 0);
  return { chips: visible, overflowCount };
}
